import React from "react";
import "@fontsource-variable/dm-sans/opsz.css";
import {
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceFieldSx,
  invoiceModalActionSx,
  invoiceOutlinedActionSx,
} from "../Invoicing/InvoiceDesignSystem";

export const prospectDesignFontFamily = '"DM Sans Variable", sans-serif';

export const prospectCardClassName =
  "flex min-w-0 flex-col gap-[22px] rounded-xl border border-[#E0E0E0] bg-white px-5 py-[26px] md:px-7";

export const prospectInputClassName =
  "box-border w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border border-[#9E9E9E] bg-white px-[14px] py-3 text-[14px] font-normal leading-[normal] text-[#0A0A0A] outline-none placeholder:text-[#9E9E9E] focus:border-[#E1A731] disabled:cursor-not-allowed disabled:bg-[#F7F7F8] disabled:text-[#9E9E9E]";

export const prospectNoOptionsSx = {
  color: "#727272",
  display: "block",
  fontWeight: 400,
  lineHeight: "normal",
  minHeight: "auto !important",
  overflow: "visible",
  px: "12px",
  py: "12px",
  textOverflow: "clip",
  whiteSpace: "normal",
  "&.Mui-disabled": { opacity: 1 },
} as const;

export const prospectSelectMenuSx = {
  color: "#000000",
  "& .MuiInputBase-root": { color: "#000000" },
  "&& .MuiOutlinedInput-root": {
    height: "auto",
    minHeight: "0 !important",
    padding: 0,
    lineHeight: "20px",
    letterSpacing: "normal",
    "& fieldset": { display: "none" },
  },
  "&& .MuiInputBase-input": {
    boxSizing: "border-box",
    height: "auto",
    border: "1px solid #9E9E9E",
    borderRadius: "8px",
    padding: "8px 12px",
    lineHeight: "20px",
    letterSpacing: "normal",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiInputBase-input::placeholder": { color: "#9E9E9E", opacity: 1 },
} as const;

export const prospectSelectSx = {
  "& button[data-invoice-select-trigger]": {
    height: "auto",
    minHeight: 0,
  },
  "& [data-invoice-select-trigger].Mui-disabled": {
    cursor: "not-allowed",
    pointerEvents: "auto",
  },
} as const;

export const prospectFieldSx = {
  ...invoiceFieldSx,
  "& .MuiInputBase-root": {
    ...invoiceFieldSx["& .MuiInputBase-root"],
    minHeight: 44,
    lineHeight: "normal",
  },
  "& .MuiInputBase-input": {
    ...invoiceFieldSx["& .MuiInputBase-input"],
    padding: "12px 14px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9E9E9E",
    opacity: 1,
  },
} as const;

export const prospectGoldButtonSx = {
  ...invoiceModalActionSx({
    backgroundColor: invoiceDesignTokens.gold,
    hoverBackgroundColor: invoiceDesignTokens.goldDark,
  }),
  boxShadow: "none",
  letterSpacing: "normal",
  lineHeight: "normal",
  minWidth: 0,
  padding: "12px 26px",
  "&:hover": {
    backgroundColor: invoiceDesignTokens.goldDark,
    boxShadow: "none",
  },
} as const;

export const prospectOutlinedButtonSx = {
  ...invoiceOutlinedActionSx,
  borderColor: "#9E9E9E",
  letterSpacing: "normal",
  lineHeight: "normal",
  minHeight: 42,
  minWidth: 0,
  padding: "12px 26px",
  "&:hover": {
    backgroundColor: "#F7F7F8",
    borderColor: "#9E9E9E",
  },
} as const;

// These are view-only overrides. Routes, titles, and permissions stay in their
// existing owners and are supplied by each route when its header is mounted.
export const prospectHeaderStyles = {
  // The shared layout adds 16 px above desktop pages. The approved Prospect
  // surface starts at 32 px, so cancel that inset only for these routes.
  containerClassName: "md:-mt-4",
  withHeaderDivider: true,
  headerDividerClassName: "border-[#ECECEC]",
  titleClassName: "text-[28px] font-semibold leading-[1.25] text-[#0A0A0A]",
  titleStyle: { fontFamily: invoiceDesignFontFamily },
  descriptionClassName:
    "mt-[6px] max-w-[900px] text-[14px] font-normal leading-[normal] text-[#0A0A0A]",
  descriptionStyle: { fontFamily: invoiceDesignFontFamily },
  horizontalPaddingClassName: "px-4 md:px-10",
  headerSpacingClassName:
    "mx-4 pt-8 pb-4 !px-0 md:mx-10 [&>div]:gap-6 [&>div>div:first-child]:min-w-0 [&>div>div:first-child]:flex-1",
} as const;

export const prospectDialogSx = {
  "& .MuiBackdrop-root": { backgroundColor: "rgba(10,10,10,.45)" },
  "& .MuiDialog-paper": {
    border: `1px solid ${invoiceDesignTokens.modalBorder}`,
    boxSizing: "border-box",
    margin: 0,
    width: "92vw",
  },
} as const;

export const prospectDialogCloseIconOptions = {
  size: 13,
  strokeWidth: 3,
} as const;

export const prospectMatchDialogCloseIconOptions = {
  size: 14,
  strokeWidth: 2.6,
} as const;

export const prospectDialogActionSx = {
  ...prospectGoldButtonSx,
  padding: "11px 22px",
  minHeight: 42,
} as const;

export const prospectDialogCancelSx = {
  ...prospectOutlinedButtonSx,
  padding: "11px 22px",
  minHeight: 42,
} as const;

export type ProspectSortGlyphProps = Readonly<{
  active?: boolean;
  direction: "asc" | "desc";
}>;

export const ProspectSortGlyph = ({
  active = false,
  direction,
}: ProspectSortGlyphProps): React.ReactElement =>
  React.createElement(
    "svg",
    {
      "aria-hidden": true,
      width: 9,
      height: 14,
      viewBox: "0 0 9 14",
      style: { display: "block", flexShrink: 0 },
    },
    React.createElement("path", {
      d: "M4.5 1 8 5H1z",
      fill: active && direction === "asc" ? invoiceDesignTokens.ink : "#C9C9C9",
    }),
    React.createElement("path", {
      d: "M4.5 13 1 9h7z",
      fill:
        active && direction === "desc" ? invoiceDesignTokens.ink : "#C9C9C9",
    }),
  );
